import Map from 'ol/Map';
import { View } from 'ol';
import { getCenter } from 'ol/extent';
import ImageLayer from 'ol/layer/Image';
import cloneDeep from 'lodash.clonedeep';
import Static from 'ol/source/ImageStatic';
import { fromLonLat, Projection } from 'ol/proj';
import { useInterval, useTimeout } from 'usehooks-ts';
import { Button } from '@progress/kendo-react-buttons';
import { useCallback, useContext, useEffect, useRef, useState } from 'react';

import {
  GetGameLogo,
  GetGameName,
  UpdateGameStateAsync,
  UpdatePlayerStateAsync,
  UpdateTeamStateAsync
} from '../../utils/game-engine/base';
import { mergeServerPlayerState } from '../../utils/game-engine/player-merge';
import { useGame } from '../../hooks/use-game';
import { GameStatus } from '../../types/state';
import { GameContext } from '../../contexts/game';
import { TeamContext } from '../../contexts/team';
import { PlayerContext } from '../../contexts/player';
import { OverlayContext } from '../../contexts/overlay';
import ListenerManager from '../../utils/listener-manager';
import { GameDocumentContext } from '../../contexts/game-document';
import GameStatusOverlay from '../../components/game-status-overlay';
import { GetResourceValue } from '../../utils/game-document/resources';
import { DisplayLanguageContext } from '../../contexts/display-languages';
import AchievementDialog from '../../components/dialog/achievement-dialog';
import { postPlayerState } from '../../services/syncronize-state';
import { generateTitleById } from '../../utils/game-document/display-languages';
import { MapAdapter } from '../../components/map/mapAdapter';
import { isNetworkOnline } from '../../utils/indicator';

export const GameLobbyPage = () => {
  const [gameDocument] = useContext(GameDocumentContext);
  const [gameState, setGameState, hubConnection] = useContext(GameContext);
  const [player, setPlayer] = useContext(PlayerContext);
  const [, setTeam] = useContext(TeamContext);
  const [, setOverlay] = useContext(OverlayContext);
  const [displayLanguageContext] = useContext(DisplayLanguageContext);

  const [displayRequiredTeamModal, setDisplayRequiredTeamModal] =
    useState<boolean>(true);
  const { data } = useGame(gameState.gameCode!);

  let worldMap = gameDocument.gameDocument?.assets?.maps?.find(
    (m) => m.id === gameDocument.gameDocument?.rules.worldMap.mapAssId
  );

  //games stored as OSM will be defaulted to googleMaps now
  const resolvedMapType =
      worldMap?.type === 'openStreetMap' || worldMap?.type === undefined
        ? 'googleMaps'
        : worldMap.type;

    const [map] = useState<Map>(
      new Map({
        interactions: [],
        controls: [],
        layers: [
          new MapAdapter({ type: resolvedMapType } as any).baseLayer
        ]
      })
    );

  const extent = [0, 0, 1200, 680];
  const projection = new Projection({
    code: 'xkcd-image',
    units: 'pixels',
    extent: extent
  });
  const [mapIllustration] = useState<Map>(
    new Map({
      layers: [],
      target: 'map',
      controls: [],
      view: new View({
        projection: projection,
        center: getCenter(extent),
        zoom: 1,
        maxZoom: 8,
        resolution: 1
      })
    })
  );

  const mapElement = useRef<HTMLDivElement>(null);

  const syncPlayerState = useCallback(async () => {
    try {
      const response = await postPlayerState(
        gameState.gameCode!,
        player.playerState?.code!,
        JSON.stringify(player)
      );

      if (response) {
        setPlayer((prev) => mergeServerPlayerState(prev, response));
      }
    } catch (err) {
      console.error(err);
    }
  }, [gameState.gameCode, player]);

  useInterval(() => {
    isNetworkOnline() && syncPlayerState();
  }, 11000);

  useEffect(() => {
    if (isNetworkOnline()) {
      syncPlayerState();
    }
  }, []);

  useEffect(() => {
    const handleOnline = () => {
      syncPlayerState();
    };
    window.addEventListener('online', handleOnline);
    return () => {
      window.removeEventListener('online', handleOnline);
    };
  }, [syncPlayerState]);

  useInterval(() => {
    // get the current game status
    let newGameState = { ...cloneDeep(gameState) };
    let currentGameStatus = newGameState.gameState?.status;
    // only process if current game status is not pre-game or starting
    // and  the game is set to automatic start
    if (
      (currentGameStatus === 'PreGame' || currentGameStatus === 'Starting') &&
      data?.allowGameAutomaticStart
    ) {
      let now = new Date();
      let gameStartDate = new Date(gameDocument.gameDocument?.gameStartDate!);
      // if curent datetime has passed the game event start datetime
      if (now > gameStartDate) {
        let status: GameStatus = 'Running';
        setGameState((prev) =>
          UpdateGameStateAsync(prev, cloneDeep({ ...prev.gameState!, status }))
        );
      }
    }
  }, 60000);

  const TeamEnabledChatHandler = async (message: any) => {
    setTeam((prev) =>
      UpdateTeamStateAsync(prev, {
        ...prev.teamState!,
        isChatDisabled: false
      })
    );
  };

  const TeamDisabledChatHandler = async (message: any) => {
    setTeam((prev) =>
      UpdateTeamStateAsync(prev, {
        ...prev.teamState!,
        isChatDisabled: true
      })
    );
  };

  const openOverlay = () => {
    setOverlay((prevState) => ({
      ...prevState,
      drawerIsOpen: true,
      gameName: GetGameName(gameDocument, player),
      gameLogo: GetGameLogo(gameDocument, player)
    }));
  };

  useTimeout(openOverlay, 3000);

  const toggleDisplayTeamModal = () =>
    setDisplayRequiredTeamModal((prev) => !prev);

  const addRemoveTeamHandler = async (message: any) => {
    const playerResponseTeamCode = message.id?.replace(
      '00000000-0000-0000-0000-000000000000',
      ''
    );
    setPlayer((prev) =>
      UpdatePlayerStateAsync(prev, {
        ...prev.playerState!,
        teamCode: playerResponseTeamCode
      })
    );
  };

  useEffect(() => {
    syncPlayerState();
  }, []);

  useEffect(() => {
    // get the world map

    // check the world map type
    if (worldMap?.type === 'illustrated') {
      const mapImage = GetResourceValue(
        gameDocument.gameDocument!,
        worldMap?.imageResId!,
        ''
      );

      //get resolution of the image
      const img = new Image();
      img.src = mapImage;
      img.onload = () => {
        //Map Illustration
        const extent = [0, 0, img?.width!, img?.height!];
        const projection = new Projection({
          code: 'xkcd-image',
          units: 'pixels',
          extent: extent
        });

        mapIllustration.setView(
          new View({
            projection: projection,
            center: getCenter(extent),
            extent,
            zoom: 1
          })
        );

        const imageLayer = new ImageLayer({
          source: new Static({
            attributions: '',
            url: mapImage,
            projection: projection,
            imageExtent: extent
          })
        });
        let layers = mapIllustration.getAllLayers();
        layers[0] = imageLayer;
        mapIllustration.setTarget(mapElement.current!);
        mapIllustration.setLayers(layers);
      };
    } else {
      if (map && worldMap) {
        map.setTarget(mapElement.current!);
        map.getView().setZoom(worldMap.zoomLevel ?? 17);
        map
          .getView()
          .setCenter(
            fromLonLat([
              worldMap.longitude ?? 152.9043310817896,
              worldMap.latitude ?? -27.681927396540104
            ])
          );
      }
    }
  }, [map, mapElement]);

  useEffect(() => {
    const playerCode = player.playerState?.code;
    if (!hubConnection || !playerCode) return;

    const handler = (message: any) => {
      new ListenerManager({
        PlayerAddToTeam: addRemoveTeamHandler,
        PlayerRemoveFromTeam: addRemoveTeamHandler
      }).listen(message);
    };

    hubConnection.on(playerCode, handler);

    return () => hubConnection.off(playerCode, handler);
  }, [hubConnection, player.playerState?.code]);

  useEffect(() => {
    const teamCode = player.playerState?.teamCode;
    if (!hubConnection || !teamCode) return;

    const handler = (content: any) => {
      new ListenerManager({
        TeamDisabledChat: TeamDisabledChatHandler,
        TeamEnabledChat: TeamEnabledChatHandler
      }).listen(content);
    };

    hubConnection.on(teamCode, handler);

    return () => hubConnection.off(teamCode, handler);
  }, [hubConnection, player.playerState?.teamCode]);

  return (
    <>
      <div className={'canvas__map'} ref={mapElement} />
      <GameStatusOverlay />
      {gameDocument.gameDocument?.settings.preGame.isPreGameTeamRequired &&
        !player.playerState?.teamCode &&
        displayRequiredTeamModal && (
          <AchievementDialog
            priorityQueueNumber={2}
            onMaskClick={toggleDisplayTeamModal}
            onClose={toggleDisplayTeamModal}>
            <div className={'d-flex justify-content-center flex-column h-100'}>
              <span>
                {generateTitleById(
                  '0c38ed7b-a7b0-4091-bc8b-af7271652d2e',
                  gameDocument,
                  displayLanguageContext.displayLanguageSelected.resources!,
                  'game'
                ) ||
                  'This is a team game. You must be part of a team to join the game'}
                .{' '}
                <u
                  role="button"
                  onClick={() => {
                    setOverlay((prevState) => ({
                      ...prevState,
                      activeTab: 'teams',
                      drawerIsOpen: true
                    }));
                    toggleDisplayTeamModal();
                  }}>
                  {generateTitleById(
                    'fb5a1ca5-069e-4dda-aa26-a414100396da',
                    gameDocument,
                    displayLanguageContext.displayLanguageSelected.resources!,
                    'game'
                  ) || 'Select a team'}
                </u>
                .
              </span>
            </div>
          </AchievementDialog>
        )}
    </>
  );
};
